import './Invites.css';
import React, { useState } from 'react';
import { Button } from '../../components/Button/Button';
import { t } from '../../locales/translate';
import { Stack } from '../../components/Stack/Stack';
import { Avatar } from '../../components/Avatar/Avatar';
import { useRouterParam } from '../../model/history';
import { destroySession, refreshSession } from '../../model/session';
import { useAsyncValue } from '../../model/data';
import { acceptInvite, getInvite } from '../../model/invite';
import { handleError } from '../../model/notification';
import { companyPath } from '../../routes';
import { useHistory } from 'react-router-dom';
import { selectMembership } from '../../model/membership';
import { expiredDate } from '../../util/datetime';
import { ErrorPage } from '../ErrorPage/ErrorPage';
import { Gatekeeper } from '../Gatekeeper/Gatekeeper';
import { LoadingPage } from '../../components/LoadingPage/LoadingPage';
import { Message } from '../../components/Message/Message';
import { useQueryHandler } from '../../model/graphql';
import { INVITE_VIEWER_EMAIL } from '../../model/queries/invite';

export function Invites(): JSX.Element {
  const { token } = useRouterParam<{ token: string }>();
  const [data, loading, error] = useAsyncValue(() => getInvite(token), [token]);
  const [acceptLoading, setAcceptLoading] = useState(false);
  const { data: viewerData } = useQueryHandler(INVITE_VIEWER_EMAIL, { fetchPolicy: 'cache-first' });
  const currentUserEmail = viewerData?.viewer?.email.toLowerCase();
  const history = useHistory();
  const invitedEmail = data?.invitedEmail?.toLowerCase();
  const showEmailMismatchWarning = !!currentUserEmail && !!invitedEmail && currentUserEmail !== invitedEmail;

  async function handleAcceptInvite(): Promise<void> {
    setAcceptLoading(true);
    try {
      const resp = await acceptInvite(token);

      if (resp?.id && resp?.companyId) {
        selectMembership(resp.id, false);
        await refreshSession();
        history.push(companyPath(resp.companyId));
      }
    } catch (error) {
      handleError(error);
    }
    setAcceptLoading(false);
  }

  if (loading) {
    return <LoadingPage />;
  }

  if (!data || error) {
    return (
      <ErrorPage
        error={new Error(t.model.membership.invalidLink)}
        action={<Button type="primary" title="Logout" block onClick={() => destroySession()} />}
      />
    );
  }

  if (data && expiredDate(data.expiresAt)) {
    return (
      <ErrorPage
        error={
          new Error(
            t.model.membership.expiredLink([data.invitedBy.name, `(${data.invitedBy.email})`].filter(Boolean).join(' '))
          )
        }
        action={<Button type="primary" title="Logout" block onClick={() => destroySession()} />}
      />
    );
  }

  return (
    <Gatekeeper>
      <Stack size="lg" justify="center">
        <p
          dangerouslySetInnerHTML={{
            __html: t.page.signup.userHasInvitedYou(data),
          }}
        />
        <section className="invites-page__avatars">
          <Avatar name={data.invitedEmail} rounded size="xl" />
          <hr className="invites-page__separator"></hr>
          <Avatar name={data.company?.name} rounded size="xl" />
        </section>
        {showEmailMismatchWarning && (
          <Message type="warning" title={t.general.warning}>
            {t.model.membership.invitedEmailMismatch(data.invitedEmail ?? '', currentUserEmail ?? '')}
          </Message>
        )}
        <footer className="invites-page__footer">
          <Button type="primary" title="Accept invite" block onClick={handleAcceptInvite} loading={acceptLoading} />
        </footer>
      </Stack>
    </Gatekeeper>
  );
}
